import { useState, useEffect, useCallback } from 'react';

export type SimulatedRole = 'god_mode' | 'admin' | 'sovereign' | 'demi_user' | null;

const SIMULATED_ROLE_KEY = 'aether-simulated-role';

export function useSimulatedRole() {
  const [simulatedRole, setSimulatedRoleState] = useState<SimulatedRole>(() => {
    const stored = localStorage.getItem(SIMULATED_ROLE_KEY);
    if (stored && ['god_mode', 'admin', 'sovereign', 'demi_user'].includes(stored)) {
      return stored as SimulatedRole;
    }
    return null;
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem(SIMULATED_ROLE_KEY);
      if (stored && ['god_mode', 'admin', 'sovereign', 'demi_user'].includes(stored)) {
        setSimulatedRoleState(stored as SimulatedRole);
      } else {
        setSimulatedRoleState(null);
      }
    };

    // Listen for cross-tab changes
    window.addEventListener('storage', handleStorageChange);
    // Listen for same-tab changes via custom event
    window.addEventListener('aether-simulation-change', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('aether-simulation-change', handleStorageChange);
    };
  }, []);

  const setSimulatedRole = useCallback((role: SimulatedRole) => {
    if (role) {
      localStorage.setItem(SIMULATED_ROLE_KEY, role);
    } else {
      localStorage.removeItem(SIMULATED_ROLE_KEY);
    }
    setSimulatedRoleState(role);
    // Dispatch custom event for same-tab reactivity
    window.dispatchEvent(new CustomEvent('aether-simulation-change'));
  }, []);

  const clearSimulation = useCallback(() => {
    localStorage.removeItem(SIMULATED_ROLE_KEY);
    setSimulatedRoleState(null);
    // Dispatch custom event for same-tab reactivity
    window.dispatchEvent(new CustomEvent('aether-simulation-change'));
  }, []);

  const isSimulating = simulatedRole !== null;

  return {
    simulatedRole,
    setSimulatedRole,
    clearSimulation,
    isSimulating,
  };
}
